import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, apiError, setToken, loadRoles } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LanguageSelect from "@/components/LanguageSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Field = ({ id, label, type = "text", value, onChange, testid, placeholder, required = true }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
      {label}
    </Label>
    <Input
      id={id}
      type={type}
      required={required}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 h-10"
    />
  </div>
);

export default function Register() {
  const { t } = useTranslation();
  const { setUser, refreshCompany } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", org_no: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", { ...form, org_no: form.org_no || null });
      setToken(data.token, true);
      await loadRoles().catch(() => {});
      setUser(data.user);
      await refreshCompany().catch(() => {});
      // 60 dagars gratis provperiod: gå direkt in i systemet. Endast om ingen provperiod
      // kunde beviljas (t.ex. samma företag registrerat tidigare) visas abonnemangssidan.
      navigate(data.company?.has_access ? "/kalender" : "/abonnemang");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-sm">
        <div
          className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-center"
          data-testid="trial-offer"
        >
          <p className="font-heading text-lg font-bold text-blue-900">Testa Fältmästare gratis i 60 dagar</p>
          <p className="mt-1 text-sm text-blue-800">Full tillgång – inget betalkort krävs – ingen bindningstid</p>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-blue-600 font-heading text-lg font-bold text-white">
            FM
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">FältMästare</h1>
            <p className="text-xs uppercase tracking-[0.05em] text-slate-500">{t("register.subtitle")}</p>
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3">
          <LanguageSelect className="w-full" />
        </div>

        <form onSubmit={submit} data-testid="register-form" className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="mb-4 text-xl font-semibold text-slate-800">{t("register.title")}</h2>
          <div className="space-y-3">
            <Field id="company" label={t("register.company")} value={form.company_name} onChange={set("company_name")} testid="register-company-input" placeholder="Företagets namn" />
            <Field id="orgno" label={t("register.orgNo")} value={form.org_no} onChange={set("org_no")} testid="register-orgno-input" placeholder="556123-4567" required={false} />
            <Field id="name" label={t("register.name")} value={form.name} onChange={set("name")} testid="register-name-input" placeholder="Ditt namn" />
            <Field id="email" label={t("register.email")} type="email" value={form.email} onChange={set("email")} testid="register-email-input" placeholder="namn@foretag.se" />
            <Field id="password" label={t("register.password")} type="password" value={form.password} onChange={set("password")} testid="register-password-input" placeholder="Minst 8 tecken" />
          </div>

          {error && (
            <p data-testid="register-error" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="register-submit-button"
            className="mt-4 h-11 w-full bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("register.submit")}
          </Button>

          <p className="mt-4 text-sm text-slate-600">
            {t("register.haveAccount")}{" "}
            <Link to="/logga-in" data-testid="register-login-link" className="font-semibold text-blue-600 hover:text-blue-700">
              {t("login.title")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
